<template>
    <article
        class="singleModeTicket outrightTicket betTicket"
        v-bind:class="[
            `_${layout}`,
            {
                _disabled: ticket.isPendingRemoval || !isValid,
            },
        ]"
        v-bind:aria-disabled="ticket.isPendingRemoval || !isValid"
    >
        <div class="betTicket-row">
            <button
                data-testid="remove-ticket"
                type="button"
                class="betTicket-remove"
                v-bind:class="`${gaClassPrefix}_Btn_DelTicket${delTicketGaSuffix}`"
                aria-label="Remove selection"
                v-bind:disabled="isBetSlipInteractionLocked"
                v-on:click="emit('remove')"
            >
                <SboIcon name="system:cross" size="16" />
            </button>
            <div class="betTicket-info">
                <div class="betTicket-league">
                    <span class="betTicket-leagueName">{{ leagueName }}</span>
                </div>
                <div class="betTicket-match">
                    <SportIcon v-bind:sportType="ticket.sportType" class="betTicket-sport" />
                    <BetTicketLiveTag v-if="ticket.isLive" />
                    <span class="betTicket-teams">{{ teamName }}</span>
                </div>
                <div class="betTicket-line">
                    <span
                        class="ticketOddsValue betTicket-price"
                        v-bind:class="[oddsChangeClass, { negative: isOddsNegative }]"
                        data-static-odds-change="true"
                    >
                        <span class="betTicket-priceArrowWrap">
                            <SboIcon name="system:arrowDropUp" class="betTicket-priceArrow _rise" />
                            <SboIcon name="system:arrowDropDown" class="betTicket-priceArrow _drop" />
                        </span>
                        {{ displayPrice }}
                    </span>
                    <div class="betTicket-lineMain">
                        <span class="betTicket-market">{{ i18n.t('marketType_OutRight') }}</span>
                    </div>
                </div>
            </div>
        </div>
        <slot />
    </article>
</template>

<script setup lang="ts">
    import { storeToRefs } from 'pinia';
    import { computed } from 'vue';
    import BetTicketLiveTag from '@/components/betSlip/Single/Ticket/BetTicketLiveTag.vue';
    import { useOddsChangeIndicator } from '@/components/betSlip/Single/Ticket/useOddsChangeIndicator';
    import SboIcon from '@/components/common/SboIcon.vue';
    import SportIcon from '@/components/common/SportIcon.vue';
    import type { IOutrightTicket } from '@/interface/ITicket';
    import type { SingleModeTicketLayout } from '@/interface/enum';
    import { getTranslated } from '@/libraries/helpers/translationHelper';
    import { i18n } from '@/libraries/i18n';
    import { useOutrightInfoSubscriptionCallback } from '@/libraries/oddsApi/composables/useOutrightInfoSubscriptionCallback';
    import { formatMinDecimalPlace } from '@/libraries/utils/text';
    import { useSingleModeBetSlipStore } from '@/stores/betSlip/singleModeBetSlipStore';

    const props = withDefaults(defineProps<{
        ticket: IOutrightTicket;
        layout: SingleModeTicketLayout;
        isValid?: boolean;
        gaClassPrefix?: string;
        delTicketGaSuffix?: string;
    }>(), {
        isValid: true,
        gaClassPrefix: '',
        delTicketGaSuffix: '',
    });

    const emit = defineEmits<{
        remove: [];
    }>();

    const store = useSingleModeBetSlipStore();
    const { isBetSlipInteractionLocked } = storeToRefs(store);

    const leagueName = computed(() => getTranslated(props.ticket.leagueName));
    const teamName = computed(() => getTranslated(props.ticket.teamName));
    const displayPrice = computed(() => formatMinDecimalPlace(props.ticket.price, 2));
    const isOddsNegative = computed(() => props.ticket.price < 0);
    const { oddsChangeClass } = useOddsChangeIndicator(
        computed(() => props.ticket.oddsChangeStatus),
        computed(() => props.ticket.price),
    );

    const { onUpdated, onDeleted } = useOutrightInfoSubscriptionCallback({
        sportType: props.ticket.sportType,
        leagueId: props.ticket.leagueId,
        oddsId: props.ticket.id,
        isLive: props.ticket.isLive,
    });

    onUpdated(() => store.queueTicketRefresh(props.ticket.key));
    onDeleted(() => store.markTicketClosed(props.ticket.key, 'betSlip_errorMessage_oddsClosed'));
</script>

<style lang="scss" scoped>
@import "@/scss/components/singleMode/singleTicket";
</style>
